import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import ScrollFadeIn from '../components/ScrollFadeIn';
import {
  FileCheck,
  Brain,
  Scale,
  BadgeCheck,
  Mic,
  GraduationCap,
  User,
} from 'lucide-react';

interface TeamMemberData {
  name: string;
  role: string;
  dept: string;
  bullets: string[];
  photo: string; // public 경로. 파일이 없으면 자동으로 placeholder 표시
}

/**
 * 팀원 사진은 public/team/ 에 아래 파일명으로 넣으면 자동 반영됩니다.
 * 파일이 없으면 임시 placeholder가 표시됩니다.
 */
const teamMembers: TeamMemberData[] = [
  {
    name: '이도운',
    role: 'CEO · 대표',
    dept: 'KAIST 융합인재학부',
    bullets: [
      '학생회장 출신의 리더십',
      '에듀테크 창업팀 업무 경험',
      '튜터 로봇 AI 개발 역량',
      '사업·프로덕트 전반 총괄',
    ],
    photo: '/team/dowoon.jpg',
  },
  {
    name: '박정원',
    role: 'CTO · 기술총괄',
    dept: 'KAIST 산업디자인학과',
    bullets: [
      'AI Experience LAB 연구원',
      'iF Design Award · Red Dot Award 수상',
      '서울아산·세브란스병원 AI Medical 연구',
      '의학 도메인 AI 음성인식 특허 등록',
      'AI 개발·프로덕트 디자인 담당',
    ],
    photo: '/team/jeongwon.jpg',
  },
  {
    name: '윤성수',
    role: 'COO · 운영총괄',
    dept: 'KAIST 전산학부',
    bullets: [
      '딥러닝·강화학습 연구',
      'AI 플랫폼 DB 구축 경험',
      '서비스 기획·운영 총괄',
      '백엔드 구축 책임',
    ],
    photo: '/team/sungsoo.jpg',
  },
  {
    name: '박대훈',
    role: 'CSO · 전략총괄',
    dept: 'KAIST 전산학부',
    bullets: [
      '시스템 시뮬레이션 연구',
      'HW/SW 융합 연구 역량',
      '기획·전략 주도',
      '풀스택 개발 담당',
    ],
    photo: '/team/daehoon.jpg',
  },
];

/** 사진 로드 실패 시 placeholder로 대체하는 컴포넌트 */
const MemberPhoto: React.FC<{ src: string; name: string }> = ({ src, name }) => {
  const [errored, setErrored] = useState(false);

  if (errored || !src) {
    return (
      <PhotoPlaceholder aria-label={name}>
        <User />
      </PhotoPlaceholder>
    );
  }

  return (
    <PhotoImage src={src} alt={name} loading="lazy" onError={() => setErrored(true)} />
  );
};

const AboutPage: React.FC = () => {
  return (
    <PageContainer>
      <HeroSection>
        <HeroContent>
          <HeroTitle>FreakIT를 소개합니다</HeroTitle>
          <HeroDescription>
            IT와 교육의 경계를 허물고, <br />
            혁신적인 솔루션으로 미래를 만들어갑니다
          </HeroDescription>
        </HeroContent>
      </HeroSection>

      <MissionSection>
        <MissionContent>
          <SectionTitle>우리의 미션</SectionTitle>
          <MissionText>
            FreakIT는 '말하고 서술하는 평가'를 AI로 자동화합니다.
            교재를 업로드하면 출처 기반 문제가 생성되고, AI가 음성으로 질문하며,
            시선·손 추적과 화면 녹화로 부정행위까지 방지합니다.
            <br /><br />
            객관식 시험에 가려져 있던 학생의 사고 과정을 다시 평가의 중심으로
            가져오는 것 — 그것이 우리가 SOCRATEST를 만든 이유입니다.
          </MissionText>
        </MissionContent>
      </MissionSection>

      <ValuesSection>
        <SectionTitle>우리의 가치관</SectionTitle>
        <ValuesGrid>
          <ScrollFadeIn direction="up" delay={0.1}>
            <ValueCard>
              <ValueIcon><FileCheck /></ValueIcon>
              <ValueTitle>근거 우선</ValueTitle>
              <ValueDescription>
                출처 없는 답은 내지 않습니다. 모든 AI 출력은 실제 교재에서
                검증된 근거 위에서만 생성됩니다.
              </ValueDescription>
            </ValueCard>
          </ScrollFadeIn>

          <ScrollFadeIn direction="up" delay={0.2}>
            <ValueCard>
              <ValueIcon><Brain /></ValueIcon>
              <ValueTitle>깊이 있는 평가</ValueTitle>
              <ValueDescription>
                측정하기 쉬운 것이 아니라 중요한 것을 측정합니다.
                정답 너머의 사고 과정을 평가합니다.
              </ValueDescription>
            </ValueCard>
          </ScrollFadeIn>

          <ScrollFadeIn direction="up" delay={0.3}>
            <ValueCard>
              <ValueIcon><Scale /></ValueIcon>
              <ValueTitle>공정성 설계</ValueTitle>
              <ValueDescription>
                같은 기준을 누구에게나 적용합니다. 투명한 기록과 부정행위
                방지로 평가의 신뢰를 지킵니다.
              </ValueDescription>
            </ValueCard>
          </ScrollFadeIn>

          <ScrollFadeIn direction="up" delay={0.4}>
            <ValueCard>
              <ValueIcon><BadgeCheck /></ValueIcon>
              <ValueTitle>현장에서 증명</ValueTitle>
              <ValueDescription>
                슬라이드가 아닌 강의실에서 검증합니다. 실제 수업 도입으로
                효용성을 입증해 갑니다.
              </ValueDescription>
            </ValueCard>
          </ScrollFadeIn>
        </ValuesGrid>
      </ValuesSection>

      <ProductsSection>
        <SectionTitle>우리의 제품</SectionTitle>
        <ProductsGrid>
          <ScrollFadeIn direction="up" delay={0.1}>
            <ProductCard>
              {/* 아이콘 교체 */}
              <ProductIcon><Mic /></ProductIcon>
              <ProductName>SOCRATEST</ProductName>
              <ProductDescription>
                AI 기반 구술 시험 플랫폼으로 PDF 자료를 업로드하면 고도화된 AI가
                서술형·구술형 문제를 자동 생성합니다. Voice AI 실시간 구술 시험,
                시선·손 추적 부정행위 방지, 전체 화면 녹화 등을 지원합니다.
              </ProductDescription>
              <ProductLink href="https://socratest.freakit.co.kr/" target="_blank" rel="noopener noreferrer">
                자세히 보기 →
              </ProductLink>
            </ProductCard>
          </ScrollFadeIn>

          <ScrollFadeIn direction="up" delay={0.2}>
            <ProductCard>
              {/* 아이콘 교체 */}
              <ProductIcon><GraduationCap /></ProductIcon>
              <ProductName>KAIStudy</ProductName>
              <ProductDescription>
                KAIST 재학생들이 직접 진행하는 프리미엄 그룹 과외 플랫폼입니다.
                검증된 강사진과 체계적인 커리큘럼으로 최고 수준의
                교육 서비스를 제공합니다.
              </ProductDescription>
              <ProductLink as={Link} to="/kaistudy">
                자세히 보기 →
              </ProductLink>
            </ProductCard>
          </ScrollFadeIn>
        </ProductsGrid>
      </ProductsSection>

      <VisionSection>
        <VisionContent>
          <SectionTitle>우리의 비전</SectionTitle>
          <VisionText>
            구술·서술 평가는 학습 효과가 가장 크지만, 채점 인력과 공정성 부담 탓에
            대규모로 적용하기 어려웠습니다.
            <br /><br />
            FreakIT는 AI로 이 장벽을 낮춰, 소수만 누리던 깊이 있는 평가를
            더 많은 학습자가 받을 수 있게 만듭니다.
            <br /><br />
            2025년 KAIST 인지신경과학 강의 도입을 시작으로, 대학·기업·교육기관으로
            적용 범위를 넓혀가고 있습니다.
          </VisionText>
        </VisionContent>
      </VisionSection>

      <TeamSection>
        <SectionTitle>우리 팀</SectionTitle>
        <TeamDescription>
          KAIST에서 만난 네 명의 창업자가 함께 FreakIT을 만들어갑니다
        </TeamDescription>
        <TeamGrid>
          {teamMembers.map((member, index) => (
            <ScrollFadeIn key={member.name} direction="up" delay={0.1 * (index + 1)}>
              <TeamMember>
                <PhotoWrap>
                  <MemberPhoto src={member.photo} name={member.name} />
                </PhotoWrap>
                <MemberName>{member.name}</MemberName>
                <MemberRole>{member.role}</MemberRole>
                <MemberDept>{member.dept}</MemberDept>
                <MemberBulletList>
                  {member.bullets.map((b, i) => (
                    <MemberBullet key={i}>{b}</MemberBullet>
                  ))}
                </MemberBulletList>
              </TeamMember>
            </ScrollFadeIn>
          ))}
        </TeamGrid>
      </TeamSection>

      <CTASection>
        <CTAContent>
          <CTATitle>FreakIT과 함께하세요</CTATitle>
          <CTADescription>
            우리의 여정에 함께하고 싶으신가요?
            <br />
            언제든 연락 주세요!
          </CTADescription>
          <CTAButton as={Link} to="/contact">
            문의하기
          </CTAButton>
        </CTAContent>
      </CTASection>
    </PageContainer>
  );
};

export default AboutPage;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const PageContainer = styled.div`
  padding-top: 80px;
`;
const HeroSection = styled.section`
  padding: 4rem 2rem;
  background: ${({ theme }) => theme.colors.bgSecondary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  text-align: center;
  color: ${({ theme }) => theme.colors.textPrimary};
`;
const HeroContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
  animation: ${fadeIn} 1s ease-out;
`;
const HeroTitle = styled.h1`
  font-size: 2.6rem;
  font-weight: 800;
  margin-bottom: 2rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 2.1rem;
  }
`;
const HeroDescription = styled.p`
  font-size: 1.5rem;
  line-height: 1.8;
  opacity: 0.95;
`;

const MissionSection = styled.section`
  padding: 4rem 2rem;
  background: ${({ theme }) => theme.colors.bgPrimary};
`;

const MissionContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
  text-align: center;
`;
const SectionTitle = styled.h2`
  font-size: 2.1rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 2.5rem;
  text-align: center;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 1.9rem;
  }
`;
const MissionText = styled.p`
  font-size: 1.25rem;
  line-height: 2;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: left;
`;
const ValuesSection = styled.section`
  padding: 4rem 2rem;
  background: ${({ theme }) => theme.colors.bgSecondary};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;
const ValuesGrid = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(260px, 100%), 1fr));
  gap: 1.75rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;
const ValueCard = styled.div`
  padding: 1.75rem;
  background: ${({ theme }) => theme.colors.bgSecondary};
  backdrop-filter: blur(12px);
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: ${({ theme }) => theme.transitions.cubic};
  &:hover {
    transform: translateY(-8px);
    box-shadow: ${({ theme }) => theme.shadows.medium};
    border-color: rgba(255, 255, 255, 0.2);
    background: rgba(15, 23, 42, 0.4);
  }
`;
const ValueIcon = styled.div`
  font-size: 1.9rem;
  margin-bottom: 1.5rem;
  color: ${({ theme }) => theme.colors.accent};
`;

const ValueTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 1rem;
`;

const ValueDescription = styled.p`
  font-size: 1rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ProductsSection = styled.section`
  padding: 4rem 2rem;
  background: transparent;
`;
const ProductsGrid = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(400px, 100%), 1fr));
  gap: 2rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;
const ProductCard = styled.div`
  padding: 2rem;
  background: ${({ theme }) => theme.colors.bgSecondary};
  backdrop-filter: blur(12px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: ${({ theme }) => theme.transitions.cubic};

  &:hover {
    transform: translateY(-8px);
    box-shadow: ${({ theme }) => theme.shadows.medium};
    border-color: rgba(255, 255, 255, 0.2);
    background: rgba(15, 23, 42, 0.4);
  }
`;
const ProductIcon = styled.div`
  font-size: 2.1rem;
  margin-bottom: 1.5rem;
  color: ${({ theme }) => theme.colors.accent};
`;

const ProductName = styled.h3`
  font-size: 2rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 1rem;
`;

const ProductDescription = styled.p`
  font-size: 1rem;
  line-height: 1.8;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 1.5rem;
`;

const ProductLink = styled.a`
  display: inline-block;
  color: ${({ theme }) => theme.colors.accent};
  font-weight: 600;
  font-size: 1rem;
  transition: ${({ theme }) => theme.transitions.fast};
  &:hover {
    padding-left: 0.5rem;
  }
`;

const VisionSection = styled.section`
  padding: 4rem 2rem;
  background: transparent;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const VisionContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
  text-align: center;
`;

const VisionText = styled.p`
  font-size: 1.25rem;
  line-height: 2;
  opacity: 0.95;
  text-align: left;
`;
const TeamSection = styled.section`
  padding: 4rem 2rem;
  background: transparent;
`;

const TeamDescription = styled.p`
  text-align: center;
  font-size: 1.25rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 2.5rem;
`;

const TeamGrid = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(280px, 100%), 1fr));
  gap: 1.75rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const TeamMember = styled.div`
  text-align: center;
  padding: 1.75rem 1.5rem;
  background: ${({ theme }) => theme.colors.bgSecondary};
  border-radius: 14px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  transition: ${({ theme }) => theme.transitions.cubic};
  height: 100%;

  &:hover {
    transform: translateY(-6px);
    box-shadow: ${({ theme }) => theme.shadows.medium};
    border-color: rgba(255, 255, 255, 0.2);
  }
`;

const PhotoWrap = styled.div`
  width: 140px;
  height: 140px;
  margin: 0 auto 1.5rem;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid rgba(255, 255, 255, 0.12);
`;

const PhotoImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const PhotoPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.4rem;
  color: rgba(255, 255, 255, 0.35);
  background: linear-gradient(135deg, #1e293b 0%, #020617 100%);
`;

const MemberName = styled.h3`
  font-size: 1.5rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 0.4rem;
`;

const MemberRole = styled.p`
  font-size: 0.95rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.accent};
  margin-bottom: 0.2rem;
`;

const MemberDept = styled.p`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.textTertiary};
  margin-bottom: 1.1rem;
`;

const MemberBulletList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const MemberBullet = styled.li`
  font-size: 0.92rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.textSecondary};
  padding-left: 1.1rem;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0.55em;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.accent};
  }
`;

const CTASection = styled.section`
  padding: 4rem 2rem;
  background: ${({ theme }) => theme.colors.bgSecondary};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const CTAContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const CTATitle = styled.h2`
  font-size: 2.1rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 1.9rem;
  }
`;
const CTADescription = styled.p`
  font-size: 1.25rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 2rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 1.15rem;
  }
`;
const CTAButton = styled.a`
  display: inline-block;
  padding: 0.9rem 2rem;
  background: #ffffff;
  color: #020617;
  border-radius: 8px;
  font-weight: 700;
  font-size: 1.2rem;
  transition: ${({ theme }) => theme.transitions.cubic};
  box-shadow: ${({ theme }) => theme.shadows.small};
  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadows.medium};
  }
`;